import type { IsoDate } from "./types.js";

/**
 * 計畫期間——前後端共用的唯一來源。
 *
 * 先前這兩個值有三個來源：前端的 domain 常數、Cloudflare Variables、
 * 以及後端兩個檔案裡的硬編碼 fallback。CLAUDE.md 甚至寫著「調整期程要
 * 同時改前端常數與 Cloudflare Variables」——那是給架構問題的操作說明。
 *
 * 現在改期程只改這裡。`PLAN_START_DATE` / `PLAN_END_DATE` 兩個環境變數已經
 * 不再讀取：一個值表達不了兩季，留著的話它會蓋過這裡，讓第一季的公斤數對
 * 監督者重新遮住而不報錯。見 `docs/adr/0008`。
 */
export interface Season {
  id: 1 | 2;
  /** 計畫期間，闖關紀錄與月曆只存在於這段期間內。 */
  start: IsoDate;
  end: IsoDate;
  /** 體重的讀取範圍。第一季比計畫期間早，因為體重在計畫開始前就已經在記錄。 */
  dataStart: IsoDate;
  dataEnd: IsoDate;
  /** 目標體重。兩季的判定方向不同，見 `CONTEXT.md`。 */
  targetWeight: number;
  /** 寫死的體重起點。第二季的起點是第一季的結算體重，那是資料，所以是 null。 */
  startWeight: number | null;
}

export const SEASON_1: Season = {
  id: 1,
  start: "2026-08-03",
  end: "2026-11-04",
  dataStart: "2026-07-01",
  dataEnd: "2026-11-04",
  targetWeight: 74.9,
  startWeight: 82.9
};

export const SEASON_2: Season = {
  id: 2,
  start: "2026-11-05",
  end: "2026-12-20",
  dataStart: "2026-11-05",
  dataEnd: "2026-12-31",
  targetWeight: 75,
  startWeight: null
};

const SEASONS: readonly Season[] = [SEASON_1, SEASON_2];

/** 今天該看哪一季。第一季之前算第一季，最後一季之後停在最後一季。 */
export function seasonOn(today: IsoDate): Season {
  return [...SEASONS].reverse().find(season => today >= season.start) || SEASON_1;
}

/** 由查詢參數找賽季，不認得回 null。用清單比對，`__proto__` 這類字串不會命中。 */
export function seasonById(raw: string): Season | null {
  return SEASONS.find(season => String(season.id) === raw) || null;
}

/** 這一天現在還能不能寫：只有今天所在賽季的日期可以。 */
export function isWritableDate(date: IsoDate, today: IsoDate): boolean {
  const season = SEASONS.find(item => today >= item.start && today <= item.end);
  return !!season && date >= season.start && date <= season.end;
}

/**
 * 這一天是不是已經封存。
 *
 * 比 `isWritableDate` 的否定寬鬆：賽季外的未來日期不算封存。捷徑的信任模型
 * 是「Apple 說什麼就是什麼」，只有會改動已公布結果的日期才擋，見 `docs/adr/0006`。
 * 第一季開始前的體重也算，它們是已公布體重線的一部分。
 */
export function isArchivedDate(date: IsoDate, today: IsoDate): boolean {
  const closed = SEASONS.filter(season => season.end < today);
  const last = closed[closed.length - 1];
  return !!last && date <= last.end;
}

/**
 * 主程式所處理的賽季。第一季的規則留在封存版與明確引用 `SEASON_1` 的地方，
 * 見 `docs/adr/0007`。
 */
export const PLAN_START: IsoDate = SEASON_2.start;
export const PLAN_END: IsoDate = SEASON_2.end;

/**
 * 目標體重斜線的終點。
 *
 * 起點不是常數：第二季的起點是第一季的結算體重，那是資料，見
 * `src/domain/measurements.ts` 的 `seasonBaseline()` 與 CONTEXT.md。
 *
 * 與期程同放 `shared/`：`linearPrediction()` 要外推到目標日，而它得在
 * 伺服器算得出來（監督者的升降方向由後端產生），`functions/` 編譯不到 `src/`。
 */
export const TARGET_END_DATE: IsoDate = PLAN_END;
export const TARGET_END_WEIGHT = SEASON_2.targetWeight;
